import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Spinner, Table, EmptyValue, ClampText, ListPage, PageHeader, TableScroll } from '@/components/ui';
import { format, parseISO } from 'date-fns';
import { usePermissions } from '../../../hooks/usePermissions';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import fuelManagementService, {
  FuelManagementRow,
  FuelSurchargeHistoryRow,
  FuelVehicleTypeKey
} from '../../../services/fuelManagementService';
import { formatDateTime } from '@/utils/displayDateTime';

/** BC sync/cron vs manual save; manual with missing name falls back to BC sync label. */
function formatFuelLastModifiedBy(row: FuelManagementRow): string {
  if (row.lastUpdatedSource === 'BC_SYNC') {
    return 'BC sync';
  }
  if (row.updatedBy) {
    const name = `${row.updatedBy.name} ${row.updatedBy.lastName}`.trim();
    if (name.length > 0) {
      return name;
    }
  }
  return 'BC sync';
}

const VEHICLE_TYPE_LABELS: Record<FuelVehicleTypeKey, string> = {
  ON_ROAD: 'On-Road',
  OFF_ROAD: 'Off-Road'
};

function formatUpdatedAt(iso: string): string {
  try {
    return formatDateTime(iso) || iso;
  } catch {
    return iso;
  }
}

function toDatetimeLocalValue(iso: string): string {
  try {
    return format(parseISO(iso), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

type DraftRow = { surcharge: string; comment: string };

const FuelManagementPage: React.FC = () => {
  useDocumentTitle('Fuel Surcharge');
  const { hasPermission } = usePermissions();
  const canView = hasPermission('fuel_management:view');
  const canEdit = hasPermission('fuel_management:edit');

  const [rows, setRows] = useState<FuelManagementRow[]>([]);
  const [historyRows, setHistoryRows] = useState<FuelSurchargeHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<number, DraftRow>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [historyModalRow, setHistoryModalRow] = useState<FuelSurchargeHistoryRow | null>(null);
  const [historyForm, setHistoryForm] = useState({
    surcharge: '',
    effectiveAtLocal: '',
    comment: ''
  });
  const [savingHistory, setSavingHistory] = useState(false);
  const [addHistoryOpen, setAddHistoryOpen] = useState(false);
  const [addHistoryForm, setAddHistoryForm] = useState({
    vehicleType: 'ON_ROAD' as FuelVehicleTypeKey,
    surcharge: '',
    effectiveAtLocal: '',
    comment: ''
  });
  const [savingAddHistory, setSavingAddHistory] = useState(false);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const [data, hist] = await Promise.all([
        fuelManagementService.list(),
        fuelManagementService.listHistory()
      ]);
      setRows(data);
      setHistoryRows(hist);
      setDrafts((prev) => {
        const merged: Record<number, DraftRow> = {};
        data.forEach((r) => {
          merged[r.id] = {
            surcharge: r.surchargePerHour,
            comment: prev[r.id]?.comment ?? ''
          };
        });
        return merged;
      });
    } catch (e) {
      console.error(e);
      setError('Failed to load fuel management settings.');
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDraftChange = (id: number, field: keyof DraftRow, value: string | boolean) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleSaveRow = async (row: FuelManagementRow) => {
    const d = drafts[row.id];
    if (!d) return;
    const parsed = parseFloat(d.surcharge);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Surcharge must be a non-negative number.');
      return;
    }
    setError(null);
    setSavingId(row.id);
    try {
      const body: {
        surchargePerHour: number;
        comment?: string | null;
      } = {
        surchargePerHour: parsed
      };
      const c = d.comment.trim();
      if (c.length > 0) {
        body.comment = c;
      }
      const updated = await fuelManagementService.updateRow(row.id, body);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setDrafts((prev) => ({
        ...prev,
        [updated.id]: {
          surcharge: updated.surchargePerHour,
          comment: ''
        }
      }));
      const hist = await fuelManagementService.listHistory();
      setHistoryRows(hist);
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Save failed.';
      setError(msg);
    } finally {
      setSavingId(null);
    }
  };

  const openHistoryEdit = (h: FuelSurchargeHistoryRow) => {
    setHistoryModalRow(h);
    setHistoryForm({
      surcharge: h.surchargePerHour,
      effectiveAtLocal: toDatetimeLocalValue(h.effectiveAt),
      comment: h.comment ?? ''
    });
  };

  const closeHistoryModal = () => {
    setHistoryModalRow(null);
    setSavingHistory(false);
  };

  const openAddHistory = () => {
    setAddHistoryForm({
      vehicleType: 'ON_ROAD',
      surcharge: '',
      effectiveAtLocal: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      comment: ''
    });
    setAddHistoryOpen(true);
    setError(null);
  };

  const closeAddHistoryModal = () => {
    setAddHistoryOpen(false);
    setSavingAddHistory(false);
  };

  const saveAddHistory = async () => {
    if (!canEdit) return;
    const parsed = parseFloat(addHistoryForm.surcharge);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Surcharge must be a non-negative number.');
      return;
    }
    if (!addHistoryForm.effectiveAtLocal) {
      setError('Effective date/time is required.');
      return;
    }
    const effectiveAt = new Date(addHistoryForm.effectiveAtLocal);
    if (Number.isNaN(effectiveAt.getTime())) {
      setError('Invalid effective date/time.');
      return;
    }
    setError(null);
    setSavingAddHistory(true);
    try {
      await fuelManagementService.createHistory({
        vehicleType: addHistoryForm.vehicleType,
        surchargePerHour: parsed,
        effectiveAt: effectiveAt.toISOString(),
        comment: addHistoryForm.comment.trim() || null
      });
      const hist = await fuelManagementService.listHistory();
      setHistoryRows(hist);
      closeAddHistoryModal();
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to add history row.';
      setError(msg);
    } finally {
      setSavingAddHistory(false);
    }
  };

  const saveHistoryModal = async () => {
    if (!historyModalRow || !canEdit) return;
    const parsed = parseFloat(historyForm.surcharge);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('Surcharge must be a non-negative number.');
      return;
    }
    if (!historyForm.effectiveAtLocal) {
      setError('Effective date/time is required.');
      return;
    }
    const effectiveAt = new Date(historyForm.effectiveAtLocal);
    if (Number.isNaN(effectiveAt.getTime())) {
      setError('Invalid effective date/time.');
      return;
    }
    setError(null);
    setSavingHistory(true);
    try {
      const updated = await fuelManagementService.updateHistory(historyModalRow.id, {
        surchargePerHour: parsed,
        effectiveAt: effectiveAt.toISOString(),
        comment: historyForm.comment.trim() || null
      });
      setHistoryRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      closeHistoryModal();
    } catch (e: unknown) {
      console.error(e);
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update history.';
      setError(msg);
    } finally {
      setSavingHistory(false);
    }
  };

  const rowDirty = (row: FuelManagementRow) => {
    const d = drafts[row.id];
    if (!d) return false;
    return d.surcharge !== row.surchargePerHour;
  };

  if (!canView) {
    return (
      <ListPage>
        <PageHeader title="Fuel Surcharge" />
        <Alert variant="warning">You do not have access to this page.</Alert>
      </ListPage>
    );
  }

  return (
    <ListPage>
      <PageHeader
        title="Fuel Surcharge"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            disabled: loading,
            onClick: () => load(),
          },
          {
            key: 'add-history',
            label: 'Add History Row',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canEdit,
            disabled: loading,
            onClick: openAddHistory,
          },
        ]}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="tw-mb-3">
          {error}
        </Alert>
      )}

      {/* Current surcharges: one row per vehicle type, so a fixed-height card above the list. */}
      <Card className="tw-mb-3">
        <Card.Body>
          <h2 className="tw-mb-1 tw-text-base tw-font-semibold tw-text-content">Current Surcharges</h2>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            Surcharge values are per hour. Manual saves show your name; automated BC Gov sync shows
            &quot;BC sync&quot;. Optional comment is stored on the new history row when the surcharge amount
            changes.
          </p>
          {loading && rows.length === 0 ? (
            <div className="tw-text-center tw-py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <div className="tw-overflow-x-auto">
              <Table striped hover className="tw-align-middle">
                <thead>
                  <tr>
                    <th>Vehicle Type</th>
                    <th>Surcharge (Per Hour)</th>
                    <th style={{ minWidth: '16rem' }}>Comment (Optional)</th>
                    <th>Last Modified By</th>
                    <th>Last Modified At</th>
                    <th style={{ width: '6rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const d = drafts[row.id];
                    return (
                      <tr key={row.id}>
                        <td>{VEHICLE_TYPE_LABELS[row.vehicleType]}</td>
                        <td style={{ maxWidth: 160 }}>
                          <Form.Control
                            type="number"
                            min={0}
                            step="0.01"
                            disabled={!canEdit}
                            aria-label={`${VEHICLE_TYPE_LABELS[row.vehicleType]} surcharge per hour`}
                            value={d?.surcharge ?? ''}
                            onChange={(e) => handleDraftChange(row.id, 'surcharge', e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            disabled={!canEdit}
                            aria-label={`${VEHICLE_TYPE_LABELS[row.vehicleType]} comment`}
                            placeholder="Reason for change (saved with surcharge update)"
                            value={d?.comment ?? ''}
                            onChange={(e) => handleDraftChange(row.id, 'comment', e.target.value)}
                          />
                        </td>
                        <td>{formatFuelLastModifiedBy(row)}</td>
                        <td>{formatUpdatedAt(row.updatedAt)}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={!canEdit || !rowDirty(row) || savingId === row.id}
                            onClick={() => handleSaveRow(row)}
                          >
                            {savingId === row.id ? 'Saving…' : 'Save'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="tw-mb-2">
        <h2 className="tw-mb-0.5 tw-text-base tw-font-semibold tw-text-content">Surcharge History</h2>
        <p className="tw-mb-0 tw-text-content-muted tw-text-sm">
          All vehicle types in one list, newest effective date first. Edit a row to correct amount,
          effective time, or comment. Existing task efforts are not changed until fuel is toggled or
          re-ingested.
        </p>
      </div>

      {/* The history list is the page's one scroll box: sticky header, Vehicle Type pinned left
          and Actions pinned right (W-H5, W-M8). */}
      <ListPage.Body aria-busy={loading}>
        <TableScroll>
          <Table size="sm" striped hover stickyHeader className="tw-align-middle tw-mb-0">
            <thead>
              <tr>
                <th className="wrap-pin-left wrap-pin-edge">Vehicle Type</th>
                <th>Effective At</th>
                <th>Surcharge ($/hr)</th>
                <th>Source</th>
                <th>Updated By</th>
                <th style={{ minWidth: '18rem' }}>Comment</th>
                <th className="wrap-pin-right" style={{ width: '5rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {historyRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="tw-py-6 tw-text-center tw-text-content-muted">
                    {loading ? 'Loading history...' : 'No history rows yet.'}
                  </td>
                </tr>
              ) : (
                historyRows.map((h) => (
                  <tr key={h.id}>
                    <td className="wrap-pin-left wrap-pin-edge">{VEHICLE_TYPE_LABELS[h.vehicleType]}</td>
                    <td>{formatUpdatedAt(h.effectiveAt)}</td>
                    <td>{h.surchargePerHour}</td>
                    <td>{h.source === 'BC_SYNC' ? 'BC sync' : 'Manual'}</td>
                    <td>
                      {h.updatedBy
                        ? `${h.updatedBy.name} ${h.updatedBy.lastName}`.trim()
                        : <EmptyValue />}
                    </td>
                    <td>
                      {h.comment?.trim() ? <ClampText>{h.comment}</ClampText> : <EmptyValue />}
                    </td>
                    <td className="wrap-pin-right">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={!canEdit}
                        onClick={() => openHistoryEdit(h)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableScroll>
      </ListPage.Body>

      <Modal show={!!historyModalRow} onHide={closeHistoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Surcharge History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyModalRow && (
            <>
              <p className="tw-text-content-muted tw-text-sm tw-mb-3">
                {VEHICLE_TYPE_LABELS[historyModalRow.vehicleType]}, effective {formatUpdatedAt(historyModalRow.effectiveAt)}
              </p>
              <Form.Group className="tw-mb-3">
                <Form.Label>Surcharge ($/hr)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step="0.01"
                  value={historyForm.surcharge}
                  onChange={(e) => setHistoryForm((f) => ({ ...f, surcharge: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="tw-mb-3">
                <Form.Label>Effective At</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={historyForm.effectiveAtLocal}
                  onChange={(e) => setHistoryForm((f) => ({ ...f, effectiveAtLocal: e.target.value }))}
                />
              </Form.Group>
              <Form.Group className="tw-mb-0">
                <Form.Label>Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={historyForm.comment}
                  onChange={(e) => setHistoryForm((f) => ({ ...f, comment: e.target.value }))}
                  placeholder="Why this value or date was set"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeHistoryModal} disabled={savingHistory}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void saveHistoryModal()} disabled={savingHistory || !canEdit}>
            {savingHistory ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={addHistoryOpen} onHide={closeAddHistoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Surcharge History Row</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            Adds a manual history entry for the chosen vehicle type at the effective time. The live
            surcharge row on the card above is not changed.
          </p>
          <Form.Group className="tw-mb-3">
            <Form.Label>Vehicle Type</Form.Label>
            <Form.Select
              value={addHistoryForm.vehicleType}
              onChange={(e) =>
                setAddHistoryForm((f) => ({
                  ...f,
                  vehicleType: e.target.value as FuelVehicleTypeKey
                }))
              }
            >
              {(Object.keys(VEHICLE_TYPE_LABELS) as FuelVehicleTypeKey[]).map((key) => (
                <option key={key} value={key}>
                  {VEHICLE_TYPE_LABELS[key]}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Surcharge ($/hr)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              step="0.01"
              value={addHistoryForm.surcharge}
              onChange={(e) => setAddHistoryForm((f) => ({ ...f, surcharge: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Effective At</Form.Label>
            <Form.Control
              type="datetime-local"
              value={addHistoryForm.effectiveAtLocal}
              onChange={(e) =>
                setAddHistoryForm((f) => ({ ...f, effectiveAtLocal: e.target.value }))
              }
            />
          </Form.Group>
          <Form.Group className="tw-mb-0">
            <Form.Label>Comment (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={addHistoryForm.comment}
              onChange={(e) => setAddHistoryForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder="Why this row was added"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddHistoryModal} disabled={savingAddHistory}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void saveAddHistory()}
            disabled={savingAddHistory || !canEdit}
          >
            {savingAddHistory ? 'Saving…' : 'Add Row'}
          </Button>
        </Modal.Footer>
      </Modal>
    </ListPage>
  );
};

export default FuelManagementPage;
